import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { modeContraindications, type Contraindication, type ModeEntry } from '@/lib/modes';
import { AdminPage, RegistryTable, Pill, FeedbackControl } from '@/admin';

const REGISTRY = 'mode_contraindications';

const SEVERITY_HEX: Record<string, string> = {
  critical: '#F2635A', high: '#F2A93B', moderate: '#F2A93B', low: '#2BBAA8', discuss: '#5B72E8',
};

export function ModeContraindicationsPage() {
  const { modeId = '' } = useParams();
  const qc = useQueryClient();
  const mq = useQuery({ queryKey: ['mode', modeId], queryFn: () => adminApi.modes.get(modeId) });
  const fb = useQuery({ queryKey: ['feedback-summary', REGISTRY], queryFn: () => feedbackApi.summary(REGISTRY) });

  const mode = (mq.data?.mode ?? {}) as ModeEntry;
  const rows = modeContraindications(mode).map((c, i) => ({ ...c, _idx: i }));
  const refresh = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', REGISTRY] });
  // Stable, unique per-row id: (mode, lens, compound, position) — the seed order is deterministic.
  const itemId = (c: Contraindication & { _idx: number }) => `${modeId}:${c.source}:${c.canonical_compound_id ?? 'x'}:${c._idx}`;

  return (
    <AdminPage
      title={`${mode.display_name || modeId} · Contraindications`}
      description="The compound contraindications this mode applies — the clinically-signable safety layer. Flag any entry for review."
      backTo={{ to: `/admin/modes/${encodeURIComponent(modeId)}`, label: mode.display_name || modeId }}
    >
      <RegistryTable<Contraindication & { _idx: number }>
        columns={[
          { key: 'compound', header: 'Compound', className: 'font-mono text-[12px]', render: (c) => c.canonical_compound_id || '—' },
          { key: 'when', header: 'Applies when', className: 'max-w-[360px] text-[12px]', render: (c) => c.applies_when_prose || (c.applies_when ? JSON.stringify(c.applies_when) : '—') },
          { key: 'severity', header: 'Severity', render: (c) => c.severity ? <Pill label={c.severity} color={SEVERITY_HEX[c.severity]} /> : <span className="text-text-muted">—</span> },
          { key: 'rationale', header: 'Rationale', className: 'max-w-[420px] text-[12px] text-text-secondary', render: (c) => c.rationale || '—' },
          { key: 'source', header: 'Lens', className: 'text-[11px] text-text-muted', render: (c) => c.source === 'mode' ? 'mode' : c.source },
        ]}
        rows={rows}
        getRowKey={(c) => itemId(c)}
        count={rows.length}
        state={{ loading: mq.isLoading, error: mq.isError }}
        emptyText="This mode declares no contraindications."
        rowActions={(c) => {
          const id = itemId(c);
          return <FeedbackControl registry={REGISTRY} itemId={id} itemLabel={`${mode.display_name || modeId}: ${c.canonical_compound_id ?? 'contraindication'}`} openCount={fb.data?.[id]?.open ?? 0} onChanged={refresh} />;
        }}
      />
    </AdminPage>
  );
}
